import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

// SPEC §11 — Arabic-capable font, self-hosted (no runtime CDN link).
import '@fontsource/noto-naskh-arabic/400.css';
import '@fontsource/noto-naskh-arabic/700.css';
import './index.css';

import { App } from './App';
import { AuthProvider } from '@/auth/auth-context';
import { AudioPlayerProvider } from '@/components/audio-player';
import { Toaster } from '@/components/ui/sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ApiError } from '@/lib/api';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error) => {
        if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
        return failureCount < 2;
      },
      refetchOnWindowFocus: false,
      staleTime: 10_000,
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('#root not found');

/**
 * The feature endpoints do not exist yet, so dev runs against the MSW handlers in
 * `src/mocks` — contract-validated fakes. Set `VITE_MSW=off` to talk to the real
 * API through the Vite proxy instead.
 */
async function enableMocking(): Promise<void> {
  if (!import.meta.env.DEV || import.meta.env.VITE_MSW === 'off') return;
  const { startMockApi } = await import('./mocks/browser');
  await startMockApi();
}

void enableMocking().then(() => {
  ReactDOM.createRoot(rootElement).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <TooltipProvider delayDuration={200}>
              <AudioPlayerProvider>
                <App />
                <Toaster />
              </AudioPlayerProvider>
            </TooltipProvider>
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </React.StrictMode>,
  );
});
